import { Services, ReadCallback, InitOptions } from 'i18next';
import { arrify, delay } from '@djs';
import { RawResourceLanguage, formatResourceLanguage } from '../utils/resourceLanguage';
import { RequiredFormatKeyPaths } from '../utils/formatKeysDefaultPaths';
import config from '../config.json';

export type BackendFallback = () => Promise<RawResourceLanguage>;

const RETRY_FETCH_DELAY = 400;

export interface BackendOptions {
  /**
   * Если не указан, используется коллбек fallback
   */
  sourceUrl?: string;
  /**
   * Альтернатинвный способ получения ресурсов переводов, применяемый
   * в случае ошибки получения переводов из sourceUrl или если sourceUrl не указан
   */
  fallback?: BackendFallback;
  /**
   * Основной префикс ключей переводов, относящихся к проекту
   */
  defaultNsPrefix?: string;
  /**
   * Переопределение путей по-умолчанию к ключам, необходимым для форматирования дат, чисел и строк
   */
  formatKeyPaths?: Partial<RequiredFormatKeyPaths>;
}

function validateBackendOptions(options: any): options is BackendOptions {
  return Boolean(
    options &&
    typeof options === 'object' &&
    ['string', 'undefined'].includes(typeof options.defaultNsPrefix) &&
    ['string', 'undefined'].includes(typeof options.sourceUrl) &&
    ['function', 'undefined'].includes(typeof options.fallback) &&
    (options.sourceUrl || options.fallback) &&
    (typeof options.formatKeyPaths === 'undefined' ||
      (options.formatKeyPaths && typeof options.formatKeyPaths === 'object')),
  );
}

class BackendError extends Error {}

/**
 * Модуль подгрузки словаря переводов ВК
 */
class Backend {
  static readonly type = 'backend';
  readonly type = 'backend';

  backendOptions: BackendOptions = {};

  services = {} as Services;

  defaultNs = 'translation'; // нэмспейс i18next по умолчанию

  /**
   * Инициализация модуля.
   * Выполняет проверку необходимых полей в backendOptions для работы модуля
   * и устанавливает необходимые параметры инициализации i18next
   */
  init(services: Services, backendOptions: any = {}, i18nextOptions: InitOptions) {
    if (validateBackendOptions(backendOptions)) {
      this.backendOptions = backendOptions;
    } else {
      throw new BackendError('Объект backend не соответствует интерфейсу BackendOptions');
    }

    this.services = services;

    services.resourceStore.options.fallbackLng = false;

    services.resourceStore.options.interpolation = {
      ...services.resourceStore.options.interpolation,
      prefix: '{',
      suffix: '}',
    };

    services.interpolator.reset();

    this.defaultNs = arrify(i18nextOptions.defaultNS || this.defaultNs)[0];
  }

  /**
   * Подгружает словарь переводов ВК и преобразует его для i18next
   */
  read(lng: string, namespace: string, callback: ReadCallback) {
    const { sourceUrl, fallback } = this.backendOptions;

    const getRawResourceLanguage = () => {
      if (sourceUrl) {
        return Backend.fetchRawResource(sourceUrl).catch((e) => fallback?.() || Promise.reject(e));
      }

      return fallback?.() || Promise.reject(new BackendError('Поле fallback у объекта backend не было получено'));
    };

    getRawResourceLanguage()
      .then((rawResourceLanguage) => {
        const resource = formatResourceLanguage(
          rawResourceLanguage,
          { ...config.formatKeysDefaultPaths, ...this.backendOptions.formatKeyPaths },
          this.defaultNs,
          this.backendOptions.defaultNsPrefix,
        );

        this.services.resourceStore.data[lng] = resource;

        callback(null, resource[namespace]);
      })
      .catch(() => {
        callback(new BackendError('Ошибка загрузки словаря через платформу переводов ВК'), false);
      });
  }

  /**
   * Скачивает словарь в платформе переводов ВК
   * @param url путь к словарю переводов
   */
  static fetchRawResource(url: string): Promise<RawResourceLanguage> {
    return fetch(url)
      .catch(() => delay(RETRY_FETCH_DELAY).then(() => fetch(url)))
      .then((response) => response.json());
  }

  create() {
    // nothing, but required by BackendModule interface
  }
}

export { Backend };
